const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

export const getNodeId = (node) =>
  String(node?.id ?? node?.value ?? node?.companyId ?? "");

export const getNodeLabel = (node) =>
  node?.companyName ?? node?.label ?? node?.orgName ?? "";

export const normalizeCustomEFSharing = (data = {}) => {
  const sharing = data.sharing ?? {};
  const shareWithinScope = Boolean(
    firstDefined(
      data.shareWithinScope,
      data.shareWithinAccount,
      sharing.shareWithinScope,
      sharing.withinScope,
      data.shareScope !== undefined ? Number(data.shareScope) === 1 : undefined,
      true,
    ),
  );
  const shareOutsideScope = shareWithinScope
    ? Boolean(
        firstDefined(
          data.shareOutsideScope,
          data.shareOutsideAccount,
          sharing.shareOutsideScope,
          sharing.outsideScope,
          Array.isArray(data.shareCompanyIds) && data.shareCompanyIds.length > 0,
          false,
        ),
      )
    : false;
  const sharedEntityIds = firstDefined(
    data.shareCompanyIds,
    data.sharedEntityIds,
    data.sharedCompanyIds,
    sharing.sharedEntityIds,
    sharing.companyIds,
    [],
  );

  return {
    shareWithinScope,
    shareOutsideScope,
    sharedEntityIds: Array.isArray(sharedEntityIds)
      ? sharedEntityIds.map(String)
      : [],
  };
};

export const isCustomEFSharingValid = (data = {}) => {
  const sharing = normalizeCustomEFSharing(data);
  return !sharing.shareOutsideScope || sharing.sharedEntityIds.length > 0;
};

export const buildCustomEFSharingPayload = (data = {}) => {
  const sharing = normalizeCustomEFSharing(data);

  return {
    shareScope: sharing.shareWithinScope ? 1 : 0,
    shareCompanyIds:
      sharing.shareWithinScope && sharing.shareOutsideScope
        ? sharing.sharedEntityIds
        : [],
  };
};

export const normalizeCustomEFPermissions = (item = {}) => {
  const isShared = Boolean(item.isShared);
  const isReadonly =
    item.isReadonly !== undefined
      ? Boolean(item.isReadonly)
      : Boolean(item.isShared);
  const isUsedByOwnerCompany = Boolean(item.isUsedByOwnerCompany);

  return {
    isShared,
    isOwner: !isShared,
    isReadonly,
    isUsedByOwnerCompany,
    canEdit: !isReadonly,
    canDelete: !isReadonly && !isUsedByOwnerCompany,
    canManageEvidence: !isReadonly,
    hasNewUpdate: item.isNewUpdateRead === false,
    creatorName: String(item.ownerUserName ?? "").trim(),
  };
};

export const buildCustomEFDropdownGroups = (
  items = [],
  currentValue,
  unavailableCurrentOption,
) => {
  const groups = {
    "Created by this account": [],
    "Shared from other accounts": [],
    "Deleted by creator": [],
  };

  (Array.isArray(items) ? items : []).forEach((item) => {
    const value = item?.value ?? item?.id;
    const isDeleted = Boolean(
      firstDefined(
        item.isDeleted,
        item.deleted,
        item.status === "deleted",
        item.deletedAt,
        false,
      ),
    );

    if (isDeleted && String(value) !== String(currentValue)) return;

    const name = item.nameEF ?? item.label ?? "";
    const permissions = normalizeCustomEFPermissions(item);
    const groupName = isDeleted
      ? "Deleted by creator"
      : permissions.isShared
        ? "Shared from other accounts"
        : "Created by this account";

    groups[groupName].push({
      ...item,
      value,
      label: name,
      groupName,
      isDeleted,
      isDisabled: isDeleted,
      isShared: !isDeleted && permissions.isShared,
      creatorName: permissions.creatorName,
      hasNewUpdate: !isDeleted && permissions.hasNewUpdate,
    });
  });

  if (
    unavailableCurrentOption?.isUnavailable &&
    currentValue !== undefined &&
    currentValue !== null &&
    currentValue !== "" &&
    !groups["Deleted by creator"].some(
      (option) => String(option.value) === String(currentValue),
    )
  ) {
    const name = unavailableCurrentOption.label || "Unavailable emission factor";
    groups["Deleted by creator"].push({
      value: currentValue,
      label: name,
      groupName: "Deleted by creator",
      isDeleted: true,
      isDisabled: true,
      isShared: false,
      creatorName: "",
      hasNewUpdate: false,
    });
  }

  return Object.entries(groups)
    .filter(([, groupOptions]) => groupOptions.length > 0)
    .map(([label, groupOptions]) => ({
      label,
      options: groupOptions,
      isCustomEFGroup: true,
    }));
};
